import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {withShade} from '@dropbox/dig-foundations';
import {Employee} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {MouseEvent, useLayoutEffect, useRef, useState} from 'react';

import {prefetchProfile} from './profiles';
import styles from './SearchBox.module.css';

const FADE = 40;

/** Fades whichever edge has more cards past it. */
const fadeMask = (left: boolean, right: boolean) =>
  `linear-gradient(to right, ${left ? 'transparent' : '#000'} 0, #000 ${FADE}px, ` +
  `#000 calc(100% - ${FADE}px), ${right ? 'transparent' : '#000'} 100%)`;

/**
 * Your people as the workstreams page's Recently viewed: filled cards, name
 * over role, you pinned first as just your face (you know who you are). One
 * line that scrolls sideways when it doesn't fit (a side pane open, a narrow
 * window), fading out at the edge with more.
 */
export const PeopleCards = ({
  people,
  you,
  activeLdap,
  onOpen,
}: {
  people: Employee[];
  /** The card the arrow keys are on: outlined. */
  activeLdap?: string;
  /** Your ldap: your card is the face alone. */
  you?: string;
  onOpen: (person: Employee, event: MouseEvent) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({left: false, right: false});

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 0;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((previous) =>
      previous.left === left && previous.right === right ? previous : {left, right}
    );
  };

  useLayoutEffect(() => {
    measure();
    const observer = new ResizeObserver(measure);
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [people.length]);

  const mask = fadeMask(edges.left, edges.right);
  return (
    <div
      ref={ref}
      onScroll={measure}
      className={styles.cardStrip}
      style={{maskImage: mask, WebkitMaskImage: mask}}
    >
      {people.map((person) => (
        <PersonCard
          key={person.ldap}
          person={person}
          isYou={person.ldap === you}
          active={person.ldap === activeLdap}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
};

/**
 * One filled card: face, then name over role (cut short, the rest on hover).
 * `isYou` is the face alone. The strip sets its width; in the pane's two-up
 * grid it fills its cell.
 */
export const PersonCard = ({
  person,
  isYou = false,
  active = false,
  onOpen,
}: {
  person: Employee;
  isYou?: boolean;
  /** The card the arrow keys are on: outlined. */
  active?: boolean;
  onOpen: (person: Employee, event: MouseEvent) => void;
}) => (
  <div
    data-row-id={`person:${person.ldap}`}
    title={isYou ? person.name : undefined}
    {...withShade({
      className: [styles.card, styles.surface, isYou && styles.youCard, active && styles.active]
        .filter(Boolean)
        .join(' '),
    })}
    onClick={(event) => onOpen(person, event)}
    onMouseEnter={() => prefetchProfile(person)}
  >
    <Avatar user={person} size="medium" />
    {!isYou && (
      <div className={styles.cardText}>
        <Text size="small" isBold tagName="div">
          <Truncate tooltipControlProps={{placement: 'bottom'}}>{person.name}</Truncate>
        </Text>
        <Text size="small" color="subtle" tagName="div">
          <Truncate tooltipControlProps={{placement: 'bottom'}}>{person.role ?? ''}</Truncate>
        </Text>
      </div>
    )}
  </div>
);
